import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect, useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import Header from "./Header";
import Footer from "./Footer";
import Icon from "./Icon";
import CartPanel from "./CartPanel";
import {
  addAddress,
  checkout,
  deleteAddress,
  errorMessage,
  fetchAddresses,
  fetchCart,
  fetchProducts,
  isAuthError,
} from "../api";
import { clearSession, setBalance, useSession } from "../session";
import { cartLines, cartTotals, money } from "../utils";
import "./CheckoutPage.css";

const MIN_ADDRESS = 20;
const MAX_ADDRESS = 128;

const Steps = () => (
  <ol className="steps" aria-label="Checkout progress">
    <li>
      <Link to="/">Cart</Link>
    </li>
    <li aria-hidden="true" className="steps-line" />
    <li aria-current="step" className="steps-current">
      <span className="steps-dot" aria-hidden="true">
        2
      </span>
      Checkout
    </li>
    <li aria-hidden="true" className="steps-line" />
    <li className="steps-next">Done</li>
  </ol>
);

const CheckoutPage = () => {
  const session = useSession();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  const [draftError, setDraftError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleError = useCallback(
    (e) => {
      if (isAuthError(e)) {
        clearSession();
        enqueueSnackbar("Your session has ended. Please log in again.", { variant: "warning" });
        history.push("/login");
      } else {
        enqueueSnackbar(errorMessage(e), { variant: "error" });
      }
    },
    [enqueueSnackbar, history]
  );

  useEffect(() => {
    if (!session.loggedIn) return;
    let active = true;
    Promise.all([fetchProducts(), fetchCart(), fetchAddresses()])
      .then(([p, c, a]) => {
        if (!active) return;
        setProducts(p);
        setCart(c);
        setAddresses(a);
        setSelected(a.length ? a[0]._id : "");
        setAdding(a.length === 0);
      })
      .catch((e) => active && handleError(e))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [session.loggedIn, handleError]);

  const lines = useMemo(() => cartLines(cart, products), [cart, products]);
  const { count, total } = cartTotals(lines);
  const enoughBalance = session.balance >= total;

  if (!session.loggedIn) {
    return <Redirect to={{ pathname: "/login", state: { from: "/checkout" } }} />;
  }

  const saveAddress = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (text.length < MIN_ADDRESS) return setDraftError(`Add a bit more detail (at least ${MIN_ADDRESS} characters).`);
    if (text.length > MAX_ADDRESS) return setDraftError(`Keep it under ${MAX_ADDRESS} characters.`);
    setBusy(true);
    try {
      const updated = await addAddress(text);
      setAddresses(updated);
      setSelected(updated[updated.length - 1]._id);
      setDraft("");
      setDraftError("");
      setAdding(false);
      enqueueSnackbar("Address saved", { variant: "success" });
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  };

  const removeAddress = async (id) => {
    setBusy(true);
    try {
      const updated = await deleteAddress(id);
      setAddresses(updated);
      if (selected === id) setSelected(updated.length ? updated[0]._id : "");
      if (!updated.length) setAdding(true);
      enqueueSnackbar("Address deleted", { variant: "info" });
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  };

  const placeOrder = async () => {
    if (!selected) {
      enqueueSnackbar("Choose a delivery address first", { variant: "warning" });
      return;
    }
    if (!enoughBalance) {
      enqueueSnackbar("Your wallet balance isn't enough for this order", { variant: "warning" });
      return;
    }
    setBusy(true);
    try {
      await checkout(selected);
      const remaining = session.balance - total;
      setBalance(remaining);
      history.push("/thanks", { total, items: count, remaining });
    } catch (e) {
      handleError(e);
      setBusy(false);
    }
  };

  const draftLength = draft.trim().length;

  return (
    <>
      <Header>
        <Steps />
      </Header>
      <main className="page container checkout" id="main">
        <Link to="/" className="back-link">
          <Icon name="arrowLeft" size={16} /> Back to shopping
        </Link>

        {loading ? (
          <div className="checkout-grid" aria-busy="true">
            <div className="checkout-main">
              <div className="card checkout-section skeleton" style={{ height: 240 }} />
              <div className="card checkout-section skeleton" style={{ height: 120 }} />
            </div>
            <div className="card checkout-summary skeleton" style={{ height: 320 }} />
          </div>
        ) : lines.length === 0 ? (
          <div className="card state-box">
            <h1>Your cart is empty</h1>
            <p>Add a few products before checking out.</p>
            <Link to="/" className="btn btn-primary">
              Browse products
            </Link>
          </div>
        ) : (
          <div className="checkout-grid">
            <div className="checkout-main">
              <section className="card checkout-section" aria-labelledby="address-title">
                <div className="section-head">
                  <h1 id="address-title" className="section-title">
                    Delivery address
                  </h1>
                  {!adding && (
                    <button type="button" className="btn btn-ghost" onClick={() => setAdding(true)}>
                      <Icon name="plus" size={16} /> New address
                    </button>
                  )}
                </div>

                {addresses.length > 0 && (
                  <fieldset className="address-list">
                    <legend className="sr-only">Saved addresses</legend>
                    {addresses.map((a, i) => (
                      <div key={a._id} className={`address ${selected === a._id ? "is-selected" : ""}`}>
                        <label className="address-choice">
                          <input
                            type="radio"
                            name="address"
                            value={a._id}
                            checked={selected === a._id}
                            onChange={() => setSelected(a._id)}
                          />
                          <span className="address-text">
                            <span className="address-name">Address {i + 1}</span>
                            <span>{a.address}</span>
                          </span>
                        </label>
                        <button
                          type="button"
                          className="link-btn address-delete"
                          disabled={busy}
                          onClick={() => removeAddress(a._id)}
                        >
                          <Icon name="trash" size={16} />
                          <span className="sr-only">Delete address {i + 1}</span>
                          <span aria-hidden="true">Delete</span>
                        </button>
                      </div>
                    ))}
                  </fieldset>
                )}

                {adding && (
                  <form className="address-form" onSubmit={saveAddress} noValidate>
                    <label htmlFor="new-address" className="field-label">
                      {addresses.length ? "Add a new address" : "Where should we deliver?"}
                    </label>
                    <textarea
                      id="new-address"
                      rows={3}
                      className={`input address-input ${draftError ? "is-invalid" : ""}`}
                      placeholder="House / flat, street, area, city, PIN code"
                      value={draft}
                      maxLength={MAX_ADDRESS + 20}
                      onChange={(e) => {
                        setDraft(e.target.value);
                        if (draftError) setDraftError("");
                      }}
                      aria-invalid={Boolean(draftError)}
                      aria-describedby="address-help"
                    />
                    <div className="address-form-foot">
                      <span id="address-help" className={draftError ? "field-error" : "field-help"}>
                        {draftError || `Between ${MIN_ADDRESS} and ${MAX_ADDRESS} characters · ${draftLength}/${MAX_ADDRESS}`}
                      </span>
                      <div className="address-form-actions">
                        {addresses.length > 0 && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => {
                              setAdding(false);
                              setDraft("");
                              setDraftError("");
                            }}
                          >
                            Cancel
                          </button>
                        )}
                        <button type="submit" className="btn btn-dark" disabled={busy}>
                          Save address
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </section>

              <section className="card checkout-section" aria-labelledby="payment-title">
                <h2 id="payment-title" className="section-title">
                  Payment
                </h2>
                <div className={`wallet-option ${enoughBalance ? "" : "is-short"}`}>
                  <span className="wallet-option-icon" aria-hidden="true">
                    <Icon name="wallet" size={22} strokeWidth={1.8} />
                  </span>
                  <span className="wallet-option-text">
                    <span className="wallet-option-name">QKart wallet</span>
                    <span>
                      Balance {money(session.balance)}
                      {enoughBalance
                        ? ` · ${money(session.balance - total)} after this order`
                        : ` · ${money(total - session.balance)} short for this order`}
                    </span>
                  </span>
                </div>
              </section>
            </div>

            <aside className="card checkout-summary" aria-labelledby="summary-title">
              <CartPanel
                lines={lines}
                readOnly
                headingId="summary-title"
                footer={
                  <>
                    <button
                      type="button"
                      className="btn btn-primary btn-lg btn-block"
                      disabled={busy || !selected || !enoughBalance}
                      onClick={placeOrder}
                    >
                      {busy ? "Placing order…" : `Place order · ${money(total)}`}
                    </button>
                    <p className="cart-note">
                      {!selected
                        ? "Add a delivery address to continue."
                        : !enoughBalance
                        ? "Not enough wallet balance. Remove some items to continue."
                        : "Paid with your QKart wallet."}
                    </p>
                    <Link to="/" className="link-btn summary-edit">
                      Edit cart
                    </Link>
                  </>
                }
              />
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
};

export default CheckoutPage;
